// What to do when the user / universes calls behind useGetUserData fail (#1348).
//
// Only a definite "not signed in" answer (401 or 403 from those endpoints) counts as signed out.
// A signed-out visitor is not an error: TED answers /API/v2/user with a 200 visitor user. A 429
// (the rate limiter), a 5xx, a timeout, a network error or an answer without a user says nothing
// about the session, so it must not send anyone to /login: a member keeps the user already loaded,
// a first load renders as signed out, and the call is retried with backoff until it heals.
import { retryAfterSeconds } from "./rateLimitMessage";

/** Routes a signed-out visitor may not stay on. Every other page is public and renders for a visitor. */
export const SIGNED_IN_ONLY_ROUTES = ["/account"];

/** Backoff between retries of a transient failure; after the last one the hook stops trying. */
export const USER_CALL_RETRY_DELAYS_MS = [2000, 5000, 15000];

/** A Retry-After longer than this is not waited out in the page; the next page load tries again. */
export const MAX_RETRY_AFTER_MS = 15 * 60 * 1000;

/** Thrown by the user / universes controllers when an answer resolves (validateStatus < 500) without a user. */
export class UserCallError extends Error {
    status: number | undefined;
    retryAfterSeconds: number | null;

    constructor(message: string, status?: number, retryAfter: number | null = null) {
        super(message);
        this.name = "UserCallError";
        this.status = status;
        this.retryAfterSeconds = retryAfter;
    }
}

/** The error a controller throws for an answer that resolved without a user. */
export const userCallError = (res: { status?: number; data?: unknown; headers?: unknown }): UserCallError => new UserCallError("No user identified.", res?.status, res?.status === 429 ? retryAfterSeconds(res?.data, res?.headers) : null);

export type UserCallFailure = { kind: "signed-out" } | { kind: "transient"; retryAfterMs: number | null };

const SIGNED_OUT_STATUSES = [401, 403];

export const classifyUserCallFailure = (error: unknown): UserCallFailure => {
    const err = error as { status?: unknown; retryAfterSeconds?: unknown; response?: { status?: unknown; data?: unknown; headers?: unknown } } | null | undefined;
    const status = typeof err?.status === "number" ? err.status : typeof err?.response?.status === "number" ? err.response.status : undefined;
    if (status !== undefined && SIGNED_OUT_STATUSES.includes(status)) return { kind: "signed-out" };
    const seconds = typeof err?.retryAfterSeconds === "number" ? err.retryAfterSeconds : status === 429 ? retryAfterSeconds(err?.response?.data, err?.response?.headers) : null;
    return { kind: "transient", retryAfterMs: seconds && seconds > 0 ? seconds * 1000 : null };
};

export const isSignedInOnlyRoute = (pathname: string, routes: string[] = SIGNED_IN_ONLY_ROUTES): boolean => routes.some((route) => pathname === route || pathname.startsWith(route + "/"));

export const isLoginRoute = (pathname: string): boolean => pathname.includes("/login");

/** Delay before retry number `attempt` (0-based), honouring Retry-After; null once the backoff is used up. */
export const userCallRetryDelay = (attempt: number, retryAfterMs: number | null = null): number | null => {
    const backoff = USER_CALL_RETRY_DELAYS_MS[attempt];
    if (backoff === undefined) return null;
    if (retryAfterMs === null) return backoff;
    if (retryAfterMs > MAX_RETRY_AFTER_MS) return null;
    return Math.max(backoff, retryAfterMs);
};

export interface UserCallFailurePlan {
    /** Send the visitor to /login. */
    redirectToLogin: boolean;
    /** Replace the user with null (render as signed out). */
    clearUser: boolean;
    /** Drop the Cognito / Amplify keys left from a session the backend no longer knows. */
    clearAmplifyKeys: boolean;
    /** Take the full-page loader down. */
    stopLoading: boolean;
    /** Try the calls again after this many ms; null for no retry. */
    retryInMs: number | null;
}

export const planUserCallFailure = ({ error, pathname, hasUser, attempt }: { error: unknown; pathname: string; hasUser: boolean; attempt: number }): UserCallFailurePlan => {
    const failure = classifyUserCallFailure(error);

    if (failure.kind === "signed-out") {
        if (isLoginRoute(pathname)) return { redirectToLogin: false, clearUser: true, clearAmplifyKeys: true, stopLoading: true, retryInMs: null };
        if (isSignedInOnlyRoute(pathname)) return { redirectToLogin: true, clearUser: true, clearAmplifyKeys: false, stopLoading: false, retryInMs: null };
        return { redirectToLogin: false, clearUser: true, clearAmplifyKeys: false, stopLoading: true, retryInMs: null };
    }

    // Transient: never redirect. A member already loaded stays signed in; a first load renders as signed out.
    return { redirectToLogin: false, clearUser: !hasUser, clearAmplifyKeys: false, stopLoading: true, retryInMs: userCallRetryDelay(attempt, failure.retryAfterMs) };
};
